import { useEffect, useRef, useState } from "react";
import { api, fileUrl, formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, FileVideo, Image as ImageIcon, Download } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function MediaLibrary() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const inputRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/media");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    let ok = 0, fail = 0;
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok++;
      } catch (e) {
        fail++;
        toast.error(`${f.name}: ${formatApiError(e)}`);
      }
    }
    setUploading(false);
    if (ok) toast.success(`Uploaded ${ok} file${ok > 1 ? "s" : ""}`);
    if (fail === 0) load();
    else load();
  };

  const isAdminRole = user?.role === "super_admin" || user?.role === "admin" || user?.role === "property_admin";

  const downloadFile = async (m) => {
    setDownloading(m.id);
    try {
      const resp = await api.get(`/admin/files/${m.storage_path}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: m.content_type }));
      const a = document.createElement("a");
      a.href = url;
      a.download = m.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success(`${m.filename} downloaded`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setDownloading(null);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/media/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="p-6 sm:p-10">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="label-eyebrow">Media library</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight leading-none mt-2">Assets.</h1>
          <p className="text-sm text-zinc-500 mt-2">{items.length} item{items.length === 1 ? "" : "s"}</p>
        </div>
        <button
          data-testid="upload-button"
          onClick={() => inputRef.current?.click()}
          className="signage-btn-primary flex items-center gap-2"
        >
          <Upload className="h-4 w-4" /> Upload
        </button>
        <input
          ref={inputRef}
          data-testid="upload-input"
          type="file"
          multiple
          accept="image/*,video/*"
          className="hidden"
          onChange={(e) => upload(Array.from(e.target.files || []))}
        />
      </div>

      <div
        data-testid="dropzone"
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          upload(Array.from(e.dataTransfer.files || []));
        }}
        className={`mb-8 p-10 border-2 border-dashed text-center transition-colors ${dragOver ? "dropzone-active" : "border-zinc-300 bg-white"}`}
      >
        <p className="font-display text-2xl font-bold tracking-tight">
          {uploading ? "Uploading…" : "Drop images & videos here"}
        </p>
        <p className="text-sm text-zinc-500 mt-1">
          JPG · PNG · WEBP · MP4 · WEBM · up to 200 MB each
        </p>
      </div>

      {items.length === 0 ? (
        <div className="signage-card p-10 text-center text-zinc-500">
          No media yet — upload your first file to get started.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {items.map((m) => (
            <div key={m.id} className="signage-card overflow-hidden group" data-testid={`media-item-${m.id}`}>
              <div className="aspect-square bg-zinc-100 relative overflow-hidden">
                {m.type === "image" ? (
                  <img src={fileUrl(m.storage_path)} alt={m.filename} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-zinc-900 text-white">
                    <FileVideo className="h-10 w-10" />
                  </div>
                )}
                {isAdminRole && (
                  <button
                    onClick={() => downloadFile(m)}
                    disabled={downloading === m.id}
                    className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 bg-white p-1.5 border border-zinc-200 hover:bg-blue-50"
                    title="Download"
                  >
                    <Download className={`h-4 w-4 text-blue-600 ${downloading === m.id ? "animate-pulse" : ""}`} />
                  </button>
                )}
                <button
                  data-testid={`delete-media-${m.id}`}
                  onClick={() => remove(m.id)}
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 bg-white p-1.5 border border-zinc-200 hover:bg-red-50"
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4 text-red-600" />
                </button>
              </div>
              <div className="p-3">
                <p className="text-xs font-medium truncate">{m.filename}</p>
                <p className="text-[11px] text-zinc-500 uppercase tracking-wider mt-0.5">
                  {m.type} · {(m.size / 1024 / 1024).toFixed(1)} MB
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
